import type { CustomerCatalogueOption } from "@shared-core/data/customer-api-types";
import { customerLabelOf } from "@shared-core/domain/customer-option-wording";
import { Badge } from "../../../app/components/primitives/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../app/components/primitives/select";
import { cn } from "../../../app/components/primitives/utils";
import {
  hasOptionGroupHint,
  OptionGroupHint,
  optionGroupHintId,
} from "./OptionHint";

// An option group of five or more choices as a labelled dropdown (Brad,
// 2026-10-12: the tile page was too long). Ruled-out choices (R28) stay in the
// list, disabled, with the reason beside them; when the current choice is the
// ruled-out one the reason shows under the field too. The dictionary's customer
// wording replaces the labels, explains the group under its heading and the
// current choice under the field.
export function OptionSelect({
  option,
  value,
  onChange,
  unavailableCodes,
  requirementHints,
  mostPopularCode,
  className,
}: {
  option: CustomerCatalogueOption;
  value: string;
  onChange: (code: string) => void;
  /** Choices the rest of the selection rules out (R28), shown disabled. */
  unavailableCodes?: ReadonlySet<string>;
  /** Why a choice is ruled out, by code ("Needs Print: Colour + spot"). */
  requirementHints?: ReadonlyMap<string, string>;
  /** The choice to badge "Most popular"; never shown on an unavailable choice. */
  mostPopularCode?: string;
  className?: string;
}) {
  const labelId = `option-${option.key}-label`;
  const triggerId = `option-${option.key}`;
  const hintId = `${triggerId}-hint`;
  const choiceHintId = `${triggerId}-choice-hint`;
  const currentHint = unavailableCodes?.has(value)
    ? requirementHints?.get(value)
    : undefined;
  const currentChoice = option.choices.find((c) => c.code === value);
  const choiceHint = currentChoice?.customer_hint;
  const describedBy =
    [
      hasOptionGroupHint(option) ? optionGroupHintId(option) : null,
      choiceHint ? choiceHintId : null,
      currentHint ? hintId : null,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div className={cn("min-w-0 space-y-2", className)}>
      <label
        className="block text-sm font-medium"
        id={labelId}
        htmlFor={triggerId}
      >
        {customerLabelOf(option)}
      </label>
      <OptionGroupHint option={option} />
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          id={triggerId}
          aria-labelledby={labelId}
          aria-describedby={describedBy}
          aria-invalid={unavailableCodes?.has(value) || undefined}
        >
          {/* The bare label: the list's reasons and badges stay out of the closed field. */}
          <SelectValue>
            {currentChoice && customerLabelOf(currentChoice)}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-w-[calc(100vw-2rem)]">
          {option.choices.map((choice) => {
            const isUnavailable = unavailableCodes?.has(choice.code) ?? false;
            const hint = isUnavailable
              ? requirementHints?.get(choice.code)
              : undefined;
            return (
              <SelectItem
                key={choice.code}
                value={choice.code}
                disabled={isUnavailable}
              >
                {customerLabelOf(choice)}
                {hint && (
                  <span className="text-xs text-muted-foreground">
                    {" "}
                    ({hint})
                  </span>
                )}
                {mostPopularCode === choice.code && !isUnavailable && (
                  <Badge className="ml-1 px-1.5 py-0 text-[10px]">
                    Most popular
                  </Badge>
                )}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      {choiceHint && (
        <p id={choiceHintId} className="text-xs text-muted-foreground">
          {choiceHint}
        </p>
      )}
      {currentHint && (
        <p id={hintId} className="text-xs text-destructive">
          {currentHint}
        </p>
      )}
    </div>
  );
}
